// src/systems/missionSystem.ts — Fase 5 reutilizable
import { MISSIONS, getMissionDef } from '../data/missions.js';
import type { PlayerState, MissionState } from '../entities/player.js';
import { TRAINER_CONFIG } from '../data/gameConfig.js';
import { store } from '../app/state.js';
import { saveLocal } from '../app/storage.js';
import { addItem } from './inventorySystem.js';

export function getMissionState(state: PlayerState, missionId:string): MissionState | undefined {
  return state.missions.find(m=> m.id===missionId);
}

export function isMissionAvailable(state: PlayerState, defId:string): boolean {
  const def = getMissionDef(defId);
  if(!def) return false;
  const ms = getMissionState(state, defId);
  if(!ms) return false;
  if(ms.status!=='disponible' && ms.status!=='bloqueada') return false;
  if(def.requiredTrainerLevel && state.trainer.level < def.requiredTrainerLevel) return false;
  if(def.requiredFlags && def.requiredFlags.some(f=> !state.world.flags[f])) return false;
  // desbloquear si completó previas? simple: primer_descubrimiento y primer_vinculo disponibles, resto bloqueado hasta anterior completada
  if(def.id==='ayuda_investigador'){
    const prev = getMissionState(state, 'primer_vinculo');
    if(prev?.status!=='completada' && prev?.status!=='entregada') return false;
  }
  if(def.id==='entrenamiento_mira'){
    const prev = getMissionState(state, 'primer_descubrimiento');
    if(prev?.status!=='completada' && prev?.status!=='entregada') return false;
  }
  if(def.id==='coleccionista_aqua'){
    const prev = getMissionState(state, 'ayuda_investigador');
    if(prev?.status!=='completada' && prev?.status!=='entregada') return false;
  }
  return ms?.status==='disponible' || ms?.status==='bloqueada';
}

export function startMission(state: PlayerState, missionId:string): boolean {
  const ms = getMissionState(state, missionId);
  const def = getMissionDef(missionId);
  if(!ms || !def) return false;
  if(ms.status!=='disponible'){
    // desbloquear si estaba bloqueada y ahora disponible
    if(ms.status==='bloqueada' && isMissionAvailable(state, missionId)){
      ms.status='disponible';
    } else return false;
  }
  ms.status='activa';
  ms.startedAt = new Date().toISOString();
  ms.progress=0;
  store.setState(state); saveLocal(state);
  return true;
}

export function updateObjective(state: PlayerState, type:string, target:string, amount=1): string[] {
  const completed: string[] = [];
  for(const ms of state.missions){
    if(ms.status!=='activa') continue;
    let changed=false;
    for(const obj of ms.objectives){
      if(obj.type===type && (obj.target===target || obj.target==='any')){
        const before = obj.current;
        obj.current = Math.min(obj.required, obj.current + amount);
        if(obj.current!==before) changed=true;
      }
    }
    if(changed){
      const total = ms.objectives.reduce((s,o)=> s+ Math.min(1,o.current/o.required),0) / ms.objectives.length;
      ms.progress = total;
      const allDone = ms.objectives.every(o=> o.current>= o.required);
      if(allDone){
        ms.status='completada';
        ms.completedAt = new Date().toISOString();
        ms.progress=1;
        completed.push(ms.id);
      }
    }
  }
  if(completed.length) { store.setState(state); saveLocal(state); }
  else {
    // guardar progreso parcial
    const anyActive = state.missions.some(m=> m.status==='activa' && m.objectives.some(o=> o.current>0));
    if(anyActive){ store.setState(state); saveLocal(state); }
  }
  return completed;
}

export function claimReward(state: PlayerState, missionId:string): boolean {
  const ms = getMissionState(state, missionId);
  const def = getMissionDef(missionId);
  if(!ms || !def) return false;
  if(ms.status!=='completada') return false;
  // recompensas
  if(def.rewards.aureos){
    state.currency += def.rewards.aureos;
    state.trainer.aureos = state.currency;
  }
  if(def.rewards.trainerXp){
    gainTrainerXp(state, def.rewards.trainerXp);
  }
  if(def.rewards.items){
    for(const it of def.rewards.items){
      addItem(state, it.id, it.qty);
    }
  }
  if(def.rewards.xpAsteria){
    // dar XP a todo el equipo
    for(const inst of [...state.team.asterias, ...state.team.boxed].slice(0,2)){
      inst.xp += def.rewards.xpAsteria!;
      // subir nivel simplificado
      while(inst.xp >= inst.xpToNext && inst.level < 50){
        inst.xp -= inst.xpToNext;
        inst.level++; inst.xpToNext = Math.floor(20*Math.pow(1.35, inst.level-1));
      }
    }
  }
  if(def.rewards.unlockFlag){
    state.world.flags[def.rewards.unlockFlag]=true;
  }
  ms.status='entregada';
  state.stats.missionsCompleted = (state.stats.missionsCompleted||0)+1;
  store.setState(state); saveLocal(state);
  return true;
}

export function gainTrainerXp(state: PlayerState, amount:number): boolean {
  state.trainer.xp += amount;
  let leveled=false;
  while(state.trainer.xp >= state.trainer.xpToNext && state.trainer.level < TRAINER_CONFIG.maxLevel){
    state.trainer.xp -= state.trainer.xpToNext;
    state.trainer.level++;
    state.trainer.xpToNext = Math.floor(TRAINER_CONFIG.baseXpToLevel * Math.pow(TRAINER_CONFIG.xpGrowth, state.trainer.level-1));
    state.profile.title = `Entrenador Nv.${state.trainer.level}`;
    leveled=true;
  }
  if(leveled){ store.setState(state); saveLocal(state); }
  return leveled;
}

// desbloquear misiones que dependen de progreso
export function refreshMissions(state: PlayerState): void {
  for(const ms of state.missions){
    if(ms.status==='bloqueada' && isMissionAvailable(state, ms.id)){
      ms.status='disponible';
    }
  }
  store.setState(state); saveLocal(state);
}
